import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { axios } from './axios';
import { endpoints } from '../constants';

/**
 * A business an app is allowed to see.
 */
export interface ConnectedAppBusiness {
  businessId: string;
  name: string;
}

/** Someone with a live connection on an app, where we are allowed to say who. */
export interface ConnectedAppPerson {
  name: string;
  email: string | null;
  isYou: boolean;
}

/**
 * How much of a business an app can read.
 *
 * `allDatasources` is the difference between "four data sources" and
 * "anything we ever connect", so it is stated on the card rather than left to
 * be inferred from a count.
 */
export interface ConnectedAppAccess {
  allDatasources: boolean;
  /** Data sources on the grant that live in a business the reader can see. */
  visibleCount: number;
  /** Every data source on the grant, including businesses the reader is not in. */
  totalCount: number;
  hiddenBusinessCount: number;
  /**
   * The KINDS of data source it can read, commonest first, with how many of
   * each — what the provider logos on a card are drawn from. Empty when the
   * app follows the whole business, where "everything" is the honest answer
   * and a row of logos would understate it.
   */
  types: Array<{ type: string; count: number }>;
}

/**
 * One app a person has connected to their ViewMetrics data.
 *
 * Shape of a single entry of `GET /mcp/connected-apps`.
 */
export interface ConnectedApp {
  /** Opaque id, sent back when disconnecting. Never shown to a person. */
  grantId: string;
  name: string;
  /** When anyone last used the app. */
  lastUsedAt: string | null;
  /** When the READER's own connection was last used — a different question. */
  yourLastUsedAt: string | null;
  connectedAt: string;
  connectedByYou: boolean;
  connectedByName: string | null;
  connectedByEmail: string | null;
  businesses: ConnectedAppBusiness[];
  access: ConnectedAppAccess;
  /** Does the reader personally hold a live connection? */
  youAreConnected: boolean;
  /** How many people are connected besides the person reading the page. */
  otherUserCount: number;
  /**
   * The subset of those we may name — colleagues in a business the reader
   * shares with this app. Always `<= otherUserCount`; the rest are counted,
   * never named.
   */
  namedOthers: ConnectedAppPerson[];
  /**
   * Whether the reader may stop the app for EVERYONE. The server decides this
   * — it requires owner/admin on every business the app can see, so an admin
   * of one client cannot cut off another. Never re-derive it from a role in
   * the UI.
   */
  canDisconnectAll: boolean;
  /**
   * Whether the reader may stop the app for THEMSELVES ONLY.
   *
   * Frequently false, and not a permissions problem: revoking one person's
   * access while leaving colleagues connected is not something the upstream
   * API can express yet, so the server reports it honestly rather than
   * offering a button that would fail. Drive the UI off this flag.
   */
  canDisconnectMe: boolean;
  /** Whether the reader may narrow what the app can read. */
  canManageAccess: boolean;
}

/**
 * The wire shape of `GET /mcp/connected-apps`, kept separate from
 * {@link ConnectedApp} so a server-side rename is a one-line change here
 * instead of a hunt through the views.
 */
interface ConnectedAppWire {
  grantId: string;
  name: string;
  logoUrl: string | null;
  lastUsedAt: string | null;
  yourLastUsedAt: string | null;
  connectedAt: string;
  businesses: Array<{ id: string; name: string }>;
  access: ConnectedAppAccess;
  connectedBy: {
    name: string | null;
    email: string | null;
    isYou: boolean;
  } | null;
  people: {
    you: boolean;
    others: number;
    named: Array<{ name: string | null; email: string | null; isYou: boolean }>;
  };
  canDisconnectMe: boolean;
  canDisconnectEveryone: boolean;
  canManageAccess: boolean;
}

const toPerson = (wire: {
  name: string | null;
  email: string | null;
  isYou: boolean;
}): ConnectedAppPerson => ({
  name: wire.name ?? wire.email ?? '',
  email: wire.email,
  isYou: wire.isYou,
});

function toConnectedApp(wire: ConnectedAppWire): ConnectedApp {
  return {
    grantId: wire.grantId,
    name: wire.name,
    lastUsedAt: wire.lastUsedAt,
    yourLastUsedAt: wire.yourLastUsedAt ?? null,
    connectedAt: wire.connectedAt,
    connectedByYou: wire.connectedBy?.isYou ?? false,
    connectedByName: wire.connectedBy?.name ?? null,
    connectedByEmail: wire.connectedBy?.email ?? null,
    businesses: (wire.businesses ?? []).map(b => ({
      businessId: b.id,
      name: b.name,
    })),
    access: {
      allDatasources: wire.access?.allDatasources ?? false,
      visibleCount: wire.access?.visibleCount ?? 0,
      totalCount: wire.access?.totalCount ?? 0,
      hiddenBusinessCount: wire.access?.hiddenBusinessCount ?? 0,
      types: wire.access?.types ?? [],
    },
    youAreConnected: wire.people?.you ?? false,
    otherUserCount: wire.people?.others ?? 0,
    namedOthers: (wire.people?.named ?? []).map(toPerson),
    canDisconnectAll: wire.canDisconnectEveryone,
    canDisconnectMe: wire.canDisconnectMe,
    canManageAccess: wire.canManageAccess ?? false,
  };
}

export interface ConnectedAppsResponse {
  apps: ConnectedAppWire[];
}

export const CONNECTED_APPS_QUERY_KEY = ['connected-apps'] as const;

/**
 * The single place that knows the shape of the connected apps API.
 * If vm-main-api's contract changes, this file is the only thing to edit.
 */
export async function fetchConnectedApps(
  signal?: AbortSignal
): Promise<ConnectedApp[]> {
  const { mcpConnectedAppsUrl } = endpoints();

  const { data } = await axios.get<ConnectedAppsResponse>(mcpConnectedAppsUrl, {
    signal,
  });

  return (data?.apps ?? []).map(toConnectedApp);
}

/** Stops the app for everyone. */
export async function disconnectAppForEveryone(grantId: string): Promise<void> {
  const { mcpConnectedAppsUrl } = endpoints();
  await axios.delete(`${mcpConnectedAppsUrl}/${grantId}`);
}

/** Stops the app for the person reading the page only. */
export async function disconnectAppForMe(grantId: string): Promise<void> {
  const { mcpConnectedAppsUrl } = endpoints();
  await axios.delete(`${mcpConnectedAppsUrl}/${grantId}/me`);
}

export const useConnectedApps = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: CONNECTED_APPS_QUERY_KEY,
    queryFn: ({ signal }) => fetchConnectedApps(signal),
    enabled,
  });

export type DisconnectScope = 'me' | 'everyone';

/**
 * Disconnects an app and refreshes the list, so the page never needs a
 * manual reload to be right.
 */
export const useDisconnectApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      grantId,
      scope,
    }: {
      grantId: string;
      scope: DisconnectScope;
    }) =>
      scope === 'everyone'
        ? disconnectAppForEveryone(grantId)
        : disconnectAppForMe(grantId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CONNECTED_APPS_QUERY_KEY }),
  });
};

// ---------------------------------------------------------------------------
// Access — what an app can read, and changing it
// ---------------------------------------------------------------------------

export interface AccessDatasource {
  /**
   * The id to send back on save: `businessId:workspaceId:accountId`.
   *
   * NOT `accountId` — an account id is only unique within a workspace, so two
   * rows in this very list can share one. The server decides the ref; the UI
   * only ever echoes it back.
   */
  ref: string;
  accountId: string;
  type: string | null;
  name: string | null;
  /** The provider login it hangs off, so two GA4 properties are tellable apart. */
  parentName: string | null;
  status: string | null;
  granted: boolean;
}

export interface AccessWorkspace {
  id: string;
  name: string;
  datasources: AccessDatasource[];
}

export interface AccessBusiness {
  id: string;
  name: string;
  role: string;
  workspaces: AccessWorkspace[];
}

export interface AppAccess {
  grantId: string;
  name: string;
  /** The app follows the business: anything connected later is included. */
  allDatasources: boolean;
  canManage: boolean;
  businesses: AccessBusiness[];
  /**
   * Data sources the app holds that this reader cannot see — another client's
   * business, or a workspace their permissions restrict. An edit never touches
   * them, and the dialog says so instead of implying the list is complete.
   */
  hiddenGrantedCount: number;
}

export const appAccessQueryKey = (grantId: string) =>
  ['connected-apps', grantId, 'access'] as const;

const accessUrl = (grantId: string) =>
  `${endpoints().mcpConnectedAppsUrl}/${grantId}/access`;

export async function fetchAppAccess(
  grantId: string,
  signal?: AbortSignal
): Promise<AppAccess> {
  const { data } = await axios.get<AppAccess>(accessUrl(grantId), { signal });
  return data;
}

export interface AppAccessUpdate {
  /** Refs, never bare account ids — see {@link AccessDatasource.ref}. */
  datasourceRefs: string[];
  allDatasources: boolean;
}

export async function updateAppAccess(
  grantId: string,
  update: AppAccessUpdate
): Promise<AppAccess> {
  const { data } = await axios.patch<AppAccess>(accessUrl(grantId), update);
  return data;
}

/** `enabled` so the dialog fetches only once it is actually open. */
export const useAppAccess = (grantId: string | undefined) =>
  useQuery({
    queryKey: appAccessQueryKey(grantId ?? ''),
    queryFn: ({ signal }) => fetchAppAccess(grantId as string, signal),
    enabled: Boolean(grantId),
  });

export const useUpdateAppAccess = (grantId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: AppAccessUpdate) =>
      updateAppAccess(grantId as string, update),
    onSuccess: data => {
      // The card shows a data source count, so the list is stale the moment
      // this succeeds.
      queryClient.setQueryData(appAccessQueryKey(data.grantId), data);
      return queryClient.invalidateQueries({
        queryKey: CONNECTED_APPS_QUERY_KEY,
      });
    },
  });
};

// ---------------------------------------------------------------------------
// The per-business kill switch: "Let apps connect to this business"
// ---------------------------------------------------------------------------

export interface BusinessConnectors {
  businessId: string;
  /** True = no app may connect, and connected ones stop answering. */
  disabled: boolean;
  /** Any member may read the switch; only owners and admins may flip it. */
  canManage: boolean;
}

export const businessConnectorsQueryKey = (businessId: string) =>
  ['business-connectors', businessId] as const;

const connectorsUrl = (businessId: string) =>
  `${endpoints().mcpBusinessConnectorsUrl}/${businessId}/connectors`;

export async function fetchBusinessConnectors(
  businessId: string,
  signal?: AbortSignal
): Promise<BusinessConnectors> {
  const { data } = await axios.get<BusinessConnectors>(
    connectorsUrl(businessId),
    { signal }
  );
  return data;
}

export const useBusinessConnectors = (businessId: string | undefined) =>
  useQuery({
    queryKey: businessConnectorsQueryKey(businessId ?? ''),
    queryFn: ({ signal }) =>
      fetchBusinessConnectors(businessId as string, signal),
    enabled: Boolean(businessId),
    // A 503 here means this deployment has no MCP at all — there is nothing
    // for a person to do about that, so do not hammer it.
    retry: false,
  });

export const useSetBusinessConnectors = (businessId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (disabled: boolean) => {
      const { data } = await axios.patch<BusinessConnectors>(
        connectorsUrl(businessId as string),
        { disabled }
      );
      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(
        businessConnectorsQueryKey(data.businessId),
        data
      );
      // Turning connectors off does not revoke anything, but it does change
      // what every listed app can currently do.
      return queryClient.invalidateQueries({
        queryKey: CONNECTED_APPS_QUERY_KEY,
      });
    },
  });
};
